"use client";

import { SpecialEvents, VideoEvents, trackEvent } from "@/analytics";
import { cn, genAbsoluteURL } from "@/components/common/utils";
import {
  VideoComedianContext,
  getRecommendedVideos,
} from "@/data/videoPlayback";
import { useToast } from "@/shadcn/ui/use-toast";
import { VideoSkeleton } from "@punchup/ui-common/components/common";
import {
  getPublicVideoURL,
  getViewingTokenForSpecial,
} from "@punchup/video/client";
import { useEffect, useRef, useState } from "react";

import ShareButton from "@/components/common/ShareButton";
import ClapReactionButton from "@/components/special/ClapReactionButton";
import SpecialAudioPlayer from "@/components/special/SpecialAudioPlayer";
import { getComedianByIDOrThrow } from "@/data/comedians";
import { getCDNImageURL } from "@/data/images";
import { SpecialRow } from "@/data/specials";
import { HeroSlot } from "@/components/persistentPlayer/HeroSlot";
import { LivestreamAndToken } from "@punchup/data/legacy/livestreaming";
import { SpecialComedianTagWithComedian } from "@punchup/data/models/special-tags";
import { VideoJS, VideoPlayerSurface } from "@punchup/video/client";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import { useFeatureGate } from "statsig-react";
import Player from "video.js/dist/types/player";
import { ComedianLink } from "./ComedianLink";
import { DescriptionDisplay } from "./DescriptionDisplay";
import { SpecialRuntimeMeta } from "./SpecialRuntimeMeta";
import SpecialAdminContextMenu from "./SpecialAdminContextMenu";
import { resolveSpecialVideoSrc } from "./resolveSpecialVideoSrc";

const LiveVideoPlayerAndWaitingRoom = dynamic(
  () =>
    import("../livestreaming/LiveVideoPlayerAndWaitingRoom").then(
      (mod) => mod.LiveVideoPlayerAndWaitingRoom
    ),
  { ssr: false, loading: () => <VideoSkeleton /> }
);

export const VideoWrapper = ({
  special,
  userCanAccessSpecial,
  posterImg,
  lastPlaybackLocation,
  context,
  className,
  livestreamInfo,
  onSpecialChange,
  playing,
  onPlayStatusChanged,
  enableKeyboardShortcuts = false,
  taggedComedians,
  hideElementsBelowVideo = false,
  usePersistentSlot = false,
  resolveAccessClientSide = false,
  checkoutBounceHref,
}: {
  special: SpecialRow;
  userCanAccessSpecial: boolean;
  posterImg: string;
  lastPlaybackLocation: number;
  context: VideoComedianContext;
  className?: string;
  livestreamInfo: LivestreamAndToken | null;
  onSpecialChange?: (special: SpecialRow) => void;
  playing?: boolean;
  onPlayStatusChanged?: (status: "PLAYING" | "PAUSED" | "ENDED") => void;
  bundlePrice?: number;
  enableKeyboardShortcuts?: boolean;
  taggedComedians?: SpecialComedianTagWithComedian[];
  /** Hide the title/meta below the player (bundles render their own). */
  hideElementsBelowVideo?: boolean;
  /**
   * Render a persistent-player hero slot instead of an inline player. The actual
   * <video> is provided by PersistentPlayerHost (root layout); the chrome below
   * is unchanged, guaranteeing it matches this wrapper exactly.
   */
  usePersistentSlot?: boolean;
  /** Resolved server-side by the page (not useFeatureGate) so the Statsig exposure is scoped and carries the right userID. */
  resolveAccessClientSide?: boolean;
  /** Bounce target when the viewing token denies access; unset surfaces (bundles) fall back to the preview. */
  checkoutBounceHref?: string;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPreview, setIsPreview] = useState<boolean>(true);
  const { toast } = useToast();
  const [currentSpecial, setCurrentSpecial] = useState<SpecialRow>(special);
  const [currentContext, setCurrentContext] =
    useState<VideoComedianContext>(context);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [currentPosterImg, setCurrentPosterImg] = useState<string>(posterImg);
  const touchStartY = useRef(0);
  const SWIPE_THRESHOLD = 50;
  const { gate: videoCommentsGate } = useFeatureGate("special_descriptions");
  const isSpecialDescriptionsEnabled = videoCommentsGate.value ?? false;
  const router = useRouter();
  const playerRef = useRef<Player | null>(null);

  // Only the three id fields participate in access resolution; destructuring
  // them keeps the effect's deps granular (no object-identity re-runs).
  const {
    id: currentSpecialId,
    video_id: currentVideoId,
    preview_video_id: currentPreviewVideoId,
  } = currentSpecial;

  useEffect(() => {
    // Access resolution (server flag vs. client viewing token) is extracted into
    // resolveSpecialVideoSrc so the branching is unit-testable without the player.
    const run = async () => {
      const { videoSrc, isPreview, tokenError, resolvedNoAccess } =
        await resolveSpecialVideoSrc({
          special: {
            id: currentSpecialId,
            video_id: currentVideoId,
            preview_video_id: currentPreviewVideoId,
          },
          userCanAccessSpecial,
          resolveAccessClientSide,
          getViewingToken: getViewingTokenForSpecial,
          getPreviewUrl: getPublicVideoURL,
        });
      if (resolvedNoAccess && checkoutBounceHref) {
        router.push(checkoutBounceHref);
        return;
      }
      if (tokenError) {
        toast({
          title: "Oops! Something went wrong, please try again.",
          variant: "destructive",
        });
      }
      setVideoSrc(videoSrc);
      setIsPreview(isPreview);
      setIsLoading(false);
    };
    run();
  }, [
    currentSpecialId,
    currentPreviewVideoId,
    currentVideoId,
    userCanAccessSpecial,
    resolveAccessClientSide,
    checkoutBounceHref,
    router,
    toast,
  ]);

  // Keyboard controls for video player (arrow keys for skip) - only when explicitly enabled
  useEffect(() => {
    if (!enableKeyboardShortcuts) return;

    const handleKeyboardEvents = (e: KeyboardEvent) => {
      // Handle keyboard shortcuts
      const handledKeys = [
        "ArrowLeft",
        "ArrowRight",
        " ",
        "k",
        "K",
        "m",
        "M",
        "f",
        "F",
      ];
      if (!handledKeys.includes(e.key)) return;

      if (!playerRef.current) return;

      // Don't interfere if user is typing in an input/textarea/contenteditable
      const activeEl = document.activeElement;
      if (
        activeEl?.tagName === "INPUT" ||
        activeEl?.tagName === "TEXTAREA" ||
        activeEl?.getAttribute("contenteditable") === "true"
      ) {
        return;
      }

      e.preventDefault();

      const player = playerRef.current;
      const currentTime = player.currentTime() || 0;
      const duration = player.duration() || 0;

      switch (e.key) {
        case "ArrowLeft":
          // Skip backward 30 seconds
          const backwardTime = Math.max(0, currentTime - 30);
          player.currentTime(backwardTime);
          trackEvent(VideoEvents.VIDEO_SKIP_BACKWARD, {
            special_id: currentSpecial?.id,
            from_time: currentTime,
            to_time: backwardTime,
            skip_amount: 30,
            input_method: "keyboard",
          });
          break;

        case "ArrowRight":
          // Skip forward 30 seconds
          const forwardTime = Math.min(duration, currentTime + 30);
          player.currentTime(forwardTime);
          trackEvent(VideoEvents.VIDEO_SKIP_FORWARD, {
            special_id: currentSpecial?.id,
            from_time: currentTime,
            to_time: forwardTime,
            skip_amount: 30,
            input_method: "keyboard",
          });
          break;

        case " ":
        case "k":
        case "K":
          // Play/pause toggle
          if (player.paused()) {
            player.play();
            trackEvent(SpecialEvents.SPECIAL_CLICKED_PLAY, {
              special_id: currentSpecial?.id,
              input_method: "keyboard",
              shortcut_key: e.key === " " ? "spacebar" : e.key.toLowerCase(),
            });
          } else {
            player.pause();
            trackEvent(SpecialEvents.SPECIAL_CLICKED_PAUSE, {
              special_id: currentSpecial?.id,
              input_method: "keyboard",
              shortcut_key: e.key === " " ? "spacebar" : e.key.toLowerCase(),
            });
          }
          break;

        case "m":
        case "M":
          // Mute toggle
          const wasMuted = player.muted();
          player.muted(!wasMuted);
          trackEvent(VideoEvents.VIDEO_VOLUME_PANEL_OPENED, {
            special_id: currentSpecial?.id,
            action: wasMuted ? "unmuted" : "muted",
            input_method: "keyboard",
            shortcut_key: "m",
          });
          break;

        case "f":
        case "F":
          // Don't toggle fullscreen if Ctrl+F or Cmd+F (browser find)
          if (e.ctrlKey || e.metaKey) {
            return;
          }
          // Fullscreen toggle
          const wasFullscreen = player.isFullscreen();
          if (wasFullscreen) {
            player.exitFullscreen();
          } else {
            player.requestFullscreen();
          }
          trackEvent(VideoEvents.VIDEO_FULLSCREEN_CHANGED, {
            special_id: currentSpecial?.id,
            is_fullscreen: !wasFullscreen,
            input_method: "keyboard",
            shortcut_key: "f",
          });
          break;
      }
    };

    document.addEventListener("keydown", handleKeyboardEvents);
    return () => {
      document.removeEventListener("keydown", handleKeyboardEvents);
    };
  }, [enableKeyboardShortcuts, currentSpecial]);

  if (!usePersistentSlot && (isLoading || !videoSrc)) return <VideoSkeleton />;
  const videoPlayer = usePersistentSlot ? (
    <HeroSlot
      media={{
        special,
        context,
        userCanAccessSpecial,
        posterImg,
        lastPlaybackLocation,
      }}
    />
  ) : livestreamInfo && userCanAccessSpecial ? (
    <LiveVideoPlayerAndWaitingRoom
      special={currentSpecial}
      livestream={livestreamInfo.livestream}
      signedToken={livestreamInfo.signedVideoToken}
      placeholderImageUrl={currentPosterImg}
      displayName={currentContext.comedian.display_name}
    />
  ) : currentSpecial.is_audio ? (
    <SpecialAudioPlayer
      src={videoSrc!}
      special={currentSpecial}
      posterImg={currentPosterImg}
      lastPlaybackLocation={lastPlaybackLocation}
    />
  ) : (
    <VideoJS
      src={videoSrc!}
      poster={currentPosterImg}
      lastPlaybackLocation={lastPlaybackLocation}
      special={currentSpecial}
      isPreview={isPreview}
      surface={VideoPlayerSurface.ComedianSpecial}
      context={currentContext}
      isVertical={false}
      playing={playing}
      onPlayStatusChanged={onPlayStatusChanged}
      playerRef={playerRef}
      onSpecialChange={async (special) => {
        setCurrentSpecial(special);

        const posterImg = special.preview_img
          ? getCDNImageURL(special.preview_img)
          : "";
        setCurrentPosterImg(posterImg);

        // Remove the current video from recommended (since we're playing it now)
        // and then potentially fetch more recommended videos
        let recommendedVideos;
        if (
          !currentContext.recommendedVideos ||
          currentContext.recommendedVideos?.length <= 1
        ) {
          recommendedVideos = await getRecommendedVideos(special);
        } else {
          recommendedVideos = currentContext.recommendedVideos?.splice(1);
        }

        let comedian = currentContext.comedian;
        if (currentContext.comedian.id !== special.comedian_id) {
          // comedian has changed
          comedian = await getComedianByIDOrThrow(special.comedian_id);
        }

        // Update url to match new special
        window.history.pushState({}, "", `/${comedian.slug}/${special.slug}`);

        setCurrentContext({
          comedian,
          recommendedVideos,
        });
      }}
    />
  );

  const specialUrl = genAbsoluteURL(
    `/${currentContext.comedian.slug}/${currentSpecial.slug}?autoplay=1`
  );

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    const touchEndY = e.changedTouches[0].clientY;
    if (touchStartY.current - touchEndY > SWIPE_THRESHOLD) {
      if (
        currentContext.recommendedVideos &&
        currentContext.recommendedVideos.length > 0
      ) {
        onSpecialChange?.(currentContext.recommendedVideos[0]);
      }
    }
  };

  return (
    <>
      <div
        data-testid="video-wrapper"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {currentSpecial.is_audio ? (
          <div className="md:mt-6 md:px-20 xl:px-0 xl:mx-auto max-w-7xl">
            {videoPlayer}
          </div>
        ) : (
          videoPlayer
        )}
      </div>
      {!hideElementsBelowVideo && (
        <div
          className={cn("flex flex-col gap-2 my-4 md:mt-6 md:mb-8", className)}
        >
          {!currentSpecial.is_audio && (
            <h3 className="font-sans normal-case text-xl md:text-3xl font-semibold text-slate-gray-900 line-clamp-3 underline-offset-2 decoration-2 leading-snug">
              {currentSpecial?.title || ""}
            </h3>
          )}
          {isSpecialDescriptionsEnabled && currentSpecial.description && (
            <DescriptionDisplay description={currentSpecial.description} />
          )}
          <SpecialRuntimeMeta special={currentSpecial} />
          <div className="flex flex-col gap-4 md:gap-6">
            <div className="flex flex-row gap-2 md:gap-6 md:flex-row items-center">
              <ClapReactionButton
                special={currentSpecial}
                comedian={currentContext.comedian}
                className="h-[36px]" // the same height as the share button
                authenticated // this component is only rendered for authenticated users
              />
              <ShareButton
                iconColor="stroke-[#3F3F3F]"
                className="h-[36px] border-none shadow-none bg-black/6 hover:bg-black/8 focus:bg-black/10"
                label="Share"
                shareData={{
                  title: currentSpecial.title,
                  url: specialUrl,
                }}
                location="comedian-special-page"
                iconHeight={18}
                iconWidth={18}
              />
              <SpecialAdminContextMenu
                comedianId={currentContext.comedian.id}
                specialId={currentSpecial.id}
              />
            </div>
            <hr className="border-gray-200 md:hidden" />
            <div className="flex flex-row items-center justify-start gap-4">
              {currentContext.comedian && (
                <ComedianLink
                  key={currentContext.comedian.id}
                  comedian={currentContext.comedian}
                  className="mb-0"
                />
              )}
            </div>
          </div>
          {taggedComedians && taggedComedians.length > 0 && (
            <div className="flex flex-col items-start gap-2 flex-wrap">
              {taggedComedians.map((tag) => (
                <ComedianLink key={tag.id} comedian={tag.comedian} />
              ))}
            </div>
          )}
          <hr className="border-gray-200 mt-2 md:hidden" />
        </div>
      )}
    </>
  );
};
